"use client";

import { GTM } from "@/lib/analytics/analytics-index";
import { GTMEventName, GTMItem, GTMItemListID } from "@/lib/analytics/analytics-types";
import { ShopCartTransaction } from "@/lib/cart/cart-types";
import { ShopBrowsingData, ShopProductPageData } from "@/lib/shop/shop-types";
import { startCase } from "lodash";
import { useEffect, useMemo, useRef } from "react";

export function GTMItemListTracker({
    products,
    itemListID,
    page_number,
    children
}: {
    products: ShopBrowsingData[];
    itemListID: GTMItemListID;
    page_number?: number;
    children?: React.ReactNode;
}) {
    return (
        <GTMViewEventTracker
            event="view_item_list"
            items={GTM.itemsFromBrowse(products)}
            itemListID={itemListID}
            page_number={page_number}
        >
            {children}
        </GTMViewEventTracker>
    );
}

export function GTMItemTracker({
    children,
    product
}: {
    children?: React.ReactNode;
    product: ShopProductPageData;
}) {
    return (
        <GTMViewEventTracker
            event="view_item"
            items={GTM.itemsFromBrowse([product])}
            itemListID="product_page"
        >
            {children}
        </GTMViewEventTracker>
    );
}

export function GTMCartTracker({
    children,
    transactions
}: {
    children?: React.ReactNode;
    transactions: ShopCartTransaction[];
}) {
    return (
        <GTMViewEventTracker
            event="view_cart"
            items={GTM.itemsFromCart(transactions)}
            itemListID="cart"
            preventDuplicates={false}
        >
            {children}
        </GTMViewEventTracker>
    );
}

function GTMViewEventTracker({
    event,
    items,
    itemListID,
    children,
    page_number,
    preventDuplicates = true
}: {
    event: GTMEventName;
    items: GTMItem[];
    itemListID: GTMItemListID;
    preventDuplicates?: boolean;
    page_number?: number;
    children?: React.ReactNode;
}) {
    const lastSentListHash = useRef<string | null>(null);
    const sentItems = useRef<string[]>([]);

    // create hash to prevent duplicate event sends
    const currentListHash = useMemo(() => {
        if (items.length === 0 || !itemListID) {
            return null;
        }

        const itemIDs = items
            .map((item) => item.item_id)
            .sort()
            .join("-");

        return `${itemListID}::${itemIDs}`;
    }, [items, itemListID]);

    const gtmData = {
        item_list_id: itemListID,
        item_list_name: startCase(itemListID.replace("-", " ")),
        items,
        page_number
    };

    useEffect(() => {
        // prevent duplicate sends for the same list
        if (currentListHash === lastSentListHash.current) {
            return;
        }

        // prevent duplicate sends for initial items for dynamic lists
        const itemsToSend =
            preventDuplicates && gtmData.items
                ? gtmData.items.filter((item) => !sentItems.current.includes(item.item_id)!)
                : gtmData.items;

        if (itemsToSend.length === 0) return;

        // prevent duplicate sends in react strict mode
        const timer = setTimeout(() => {
            GTM.sendEvent({
                event,
                ecommerce: {
                    ...gtmData,
                    items: itemsToSend
                }
            });
            lastSentListHash.current = currentListHash;
            sentItems.current = gtmData.items!.map((item) => item.item_id);
        }, 0);

        return () => {
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentListHash]);
    return <GTM.ItemContext.Provider value={gtmData}>{children}</GTM.ItemContext.Provider>;
}
